import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useRegal } from "@/api/hooks/useRegal";
import {
  nakresliZostavu,
  pripojStylKreslenia,
  POHLADY,
  type DokumentRegalu,
  type Kresba,
  type Pohlad,
} from "@/lib/kreslenieRegalu";
import { cn } from "@/lib/utils";

export interface ZostavaNahladu {
  uid: string;
  kod: string;
  nazov: string;
  pocet: number;
}

const VYSKA_PX = 340;

/** Kľúče dielov, ktoré sú v obrázku (`data-kluc`, DOM ich vráti odkódované). */
function klucePlochy(el: Element | null): Set<string> {
  const kluce = new Set<string>();
  el?.querySelectorAll("[data-kluc]").forEach((g) => {
    const k = g.getAttribute("data-kluc");
    if (k) kluce.add(k);
  });
  return kluce;
}

/**
 * Náhľad regálu v rozpočte tovaru (KOLO 237) — ako Prehľad dielov konfigurátora:
 * obrázok vybranej zostavy (1 kus), pohľady zľava / sprava / spredu / zboku /
 * zhora; vybraný riadok rozsvieti svoje diely (ostatné stlmí), klik na diel
 * vyberie jeho riadok. User si vybral panel vpravo vedľa zostáv.
 */
export function NahladRegalu({
  variantaId,
  zostavy,
  uid,
  kluc,
  kluceRiadkov,
  onZostava,
  onKluc,
}: {
  variantaId: string;
  /** Zostavy regálu v poradí rozpočtu (`zostava.regal_kluc` = uid). */
  zostavy: ZostavaNahladu[];
  uid: string;
  /** Vybraný riadok (`dielec.regal_kluc`) — `null` = nič nesvieti. */
  kluc: string | null;
  /** Kľúče riadkov zostavy v rozpočte tovaru — diel mimo nich je diel Tandemu (nábytok). */
  kluceRiadkov: string[];
  onZostava: (uid: string) => void;
  onKluc: (kluc: string | null) => void;
}): JSX.Element {
  const stav = useRegal(variantaId);
  const dokument = (stav.data?.regal ?? null) as DokumentRegalu | null;
  const [pohlad, setPohlad] = useState<Pohlad | null>(null);
  const [kresba, setKresba] = useState<Kresba | null>(null);
  const [chyba, setChyba] = useState<string | null>(null);
  const plocha = useRef<HTMLDivElement>(null);
  const [kresliaSa, setKresliaSa] = useState(false);
  const [vObrazku, setVObrazku] = useState<Set<string>>(new Set());
  const zostava = zostavy.find((z) => z.uid === uid) ?? null;

  useEffect(pripojStylKreslenia, []);
  // iná zostava → jej predvolený pohľad (gondola zľava, roh sprava…)
  useEffect(() => setPohlad(null), [uid]);

  useEffect(() => {
    if (!dokument) return;
    let platne = true;
    setKresliaSa(true);
    nakresliZostavu(dokument, uid, pohlad, VYSKA_PX)
      .then((k) => {
        if (!platne) return;
        setKresba(k);
        setChyba(null);
      })
      .catch((e: unknown) => {
        if (!platne) return;
        setKresba(null);
        setChyba(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (platne) setKresliaSa(false);
      });
    return () => {
      platne = false;
    };
  }, [dokument, uid, pohlad]);

  // zvýraznenie: vybraný riadok svieti, ostatné diely stlmené (vykres.css)
  useEffect(() => {
    const el = plocha.current;
    const svg = el?.querySelector("svg");
    setVObrazku(klucePlochy(el));
    if (!svg) return;
    let nasiel = false;
    svg.querySelectorAll("[data-kluc]").forEach((g) => {
      const svieti = kluc !== null && g.getAttribute("data-kluc") === kluc;
      g.classList.toggle("svieti", svieti);
      nasiel ||= svieti;
    });
    svg.classList.toggle("stlmene", nasiel);
  }, [kresba, kluc]);

  const html = useMemo(() => ({ __html: kresba?.svg ?? "" }), [kresba]);
  const mimoObrazka = kluc !== null && kresba !== null && !vObrazku.has(kluc);
  // diel v obrázku bez riadku v tovare — vyrába ho Tandem, nacení rozpočtár v nábytku
  const mimoTovaru = kluc !== null && vObrazku.has(kluc) && !kluceRiadkov.includes(kluc);

  return (
    <aside
      className="flex flex-col gap-2 rounded-md border bg-card p-3 lg:sticky lg:top-0"
      aria-label="Náhľad regálu"
      data-testid="nahlad-regalu"
    >
      <div className="flex flex-wrap items-center gap-2">
        {zostavy.length > 1 ? (
          <select
            className="h-7 min-w-0 flex-1 rounded-md border bg-background px-2 text-sm"
            value={uid}
            aria-label="Zostava v náhľade"
            onChange={(e) => onZostava(e.target.value)}
          >
            {zostavy.map((z) => (
              <option key={z.uid} value={z.uid}>
                {z.kod} {z.nazov}
              </option>
            ))}
          </select>
        ) : (
          <span className="min-w-0 flex-1 truncate text-sm font-medium">
            {zostava ? `${zostava.kod} ${zostava.nazov}` : "Regál"}
          </span>
        )}
        {zostava && zostava.pocet > 1 ? (
          <span className="text-xs text-muted-foreground">1 z {zostava.pocet} ks</span>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-1" role="group" aria-label="Pohľad">
        {POHLADY.map((p) => (
          <button
            key={p.id}
            type="button"
            title={p.popis}
            aria-pressed={(kresba?.pohlad ?? pohlad) === p.id}
            onClick={() => setPohlad(p.id)}
            className={cn(
              "rounded-full border px-2.5 py-0.5 text-xs",
              (kresba?.pohlad ?? pohlad) === p.id
                ? "border-vyber bg-vyber-podklad font-semibold text-vyber-id"
                : "text-muted-foreground hover:bg-muted",
            )}
          >
            {p.nazov}
          </button>
        ))}
      </div>
      <div
        ref={plocha}
        className={cn(
          "relative flex min-h-[200px] items-center justify-center overflow-auto rounded-md bg-[#faf8f5] p-1 [&_svg]:h-auto [&_svg]:max-h-[60vh] [&_svg]:max-w-full",
          kresliaSa && kresba ? "opacity-60" : "",
        )}
        // klik na diel vyberie jeho riadok, klik mimo zruší výber
        onClick={(e) => {
          const g = (e.target as Element).closest("[data-kluc]");
          onKluc(g?.getAttribute("data-kluc") ?? null);
        }}
        data-testid="nahlad-plocha"
        dangerouslySetInnerHTML={html}
      />
      {!kresba && (kresliaSa || stav.isLoading) ? (
        <p className="flex items-center gap-2 text-xs text-muted-foreground" role="status">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          Kreslím regál…
        </p>
      ) : null}
      {chyba ? <p className="text-xs text-destructive">Náhľad sa nenakreslil: {chyba}</p> : null}
      {mimoObrazka ? (
        <p className="text-xs text-muted-foreground">
          Táto položka v obrázku nie je (LED, háčiky, doplnky a nákup konfigurátor nekreslí).
        </p>
      ) : mimoTovaru ? (
        <p className="text-xs text-muted-foreground">
          Tento diel vyrába Tandem — v tovare nie je, nacení ho rozpočtár v nábytku.
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          Klik na riadok rozsvieti diel, klik na diel vyberie riadok.
        </p>
      )}
    </aside>
  );
}
